import { useEffect, useRef } from "react";

import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import geoService, {
  convertCoordinatesToLongLat,
} from "@mapmycustomers/shared/util/geo/GeoService";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

interface UseAutoDetectCountryOptions {
  enabled: boolean;
  hasCountry: boolean;
  onCountryDetected: (country: string, countryCode: CountryCode) => void;
  onReverseGeocodeAddress: (payload: {
    callback: (result: GeocodeResult) => void;
    coordinates: LongLat;
    failureCallback?: () => void;
  }) => void;
}

const useAutoDetectCountry = ({
  enabled,
  hasCountry,
  onCountryDetected,
  onReverseGeocodeAddress,
}: UseAutoDetectCountryOptions) => {
  const alreadyDetected = useRef(false);
  const handleCountryDetected = useDynamicCallback(onCountryDetected);

  useEffect(() => {
    if (!enabled || hasCountry || alreadyDetected.current) {
      return;
    }
    alreadyDetected.current = true;
    geoService.getCurrentPosition().then((position) => {
      onReverseGeocodeAddress({
        callback: (result) => {
          if (result.address.country && result.address.countryCode) {
            handleCountryDetected(result.address.country, result.address.countryCode);
          }
        },
        coordinates: convertCoordinatesToLongLat(position.coords),
      });
    });
  }, [enabled, handleCountryDetected, hasCountry, onReverseGeocodeAddress]);
};

export default useAutoDetectCountry;
